import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LuCalendar, LuClock, LuCreditCard, LuShieldCheck } from 'react-icons/lu';
import Page from '../../Components/Page/BookingPage';
import { formatMinutesAsTime, parseTimeToMinutes } from '../../utils/time';
import { normalizeBookingServices } from '../../utils/serviceBuilder';
import styles from './paymentFailure.module.css';

function PaymentFailure() {
    const location = useLocation();
    const navigate = useNavigate();
    const state = location?.state ?? {};
    const booking = state.booking ?? {};
    const errorMessage = state.error || 'Something went wrong while saving your card. Please try again.';
    const services = normalizeBookingServices(booking);
    const canRetry = Boolean(booking.slotKey);

    const formattedDate = booking.date
        ? new Date(`${booking.date}T00:00:00`).toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        })
        : '';
    const startTime = booking.slotValue ? formatMinutesAsTime(parseTimeToMinutes(booking.slotValue)) : '';

    const handleRetry = () => {
        navigate(`/checkout/${booking.slotKey}`, { state: booking, replace: true });
    };

    return (
        <Page
            title="We couldn't save your card"
            subtitle="Your appointment request wasn't completed."
            grid={false}
            centered
        >
            <section className={styles.card}>
                <div className={styles.iconWrap} aria-hidden="true">
                    <LuCreditCard />
                </div>

                <div className={styles.error} role="alert">
                    <p className={styles.errorMessage}>{errorMessage}</p>
                    {state.code && <p className={styles.errorCode}>Reference: {state.code}</p>}
                </div>

                <div className={styles.notice}>
                    <LuShieldCheck aria-hidden="true" className={styles.noticeIcon} />
                    <p>
                        <strong>You haven't been charged.</strong> Double-check your card details, try a
                        different card, or contact your bank if the problem continues.
                    </p>
                </div>

                {(services.length > 0 || formattedDate) && (
                    <div className={styles.summary}>
                        <h2 className={styles.summaryTitle}>Your appointment</h2>
                        {services.length > 0 && (
                            <ul className={styles.services}>
                                {services.map((service) => (
                                    <li key={service.product_id}>{service.product_name}</li>
                                ))}
                            </ul>
                        )}
                        {formattedDate && (
                            <div className={styles.detailRow}>
                                <LuCalendar aria-hidden="true" className={styles.detailIcon} />
                                <span>{formattedDate}</span>
                            </div>
                        )}
                        {startTime && (
                            <div className={styles.detailRow}>
                                <LuClock aria-hidden="true" className={styles.detailIcon} />
                                <span>{startTime}{state.endTime ? ` – ${state.endTime}` : ''}</span>
                            </div>
                        )}
                    </div>
                )}

                <div className={styles.actions}>
                    {canRetry ? (
                        <button type="button" className={styles.primary} onClick={handleRetry}>
                            Try again
                        </button>
                    ) : (
                        <Link to="/book" className={styles.primary}>Book an appointment</Link>
                    )}
                    <Link to="/home" className={styles.secondary}>Back to home</Link>
                </div>
            </section>
        </Page>
    );
}

export default PaymentFailure;